import { type ApolloClient, type NormalizedCacheObject, type QueryResult, useSubscription } from "@apollo/client";
import { LockState, type Maybe } from "gql/graphql";
import { handleSetInitialData, useInitialStateQuery } from "hooks/device/useInitialStateQuery";
import { useDoorAndLockState } from "hooks/useDoorAndLockState";
import { useFetchUnknownDishTypes } from "hooks/useFetchUnknownDishTypes";
import { useMixpanelTrackers } from "hooks/useMixpanelTrackers";
import { useQrCodeLifecycleLogging } from "hooks/useQrCodeLifecycleLogging";
import { useRefetchDishTypesQuery } from "hooks/useRefetchDishTypesQuery";
import { useAppDispatch, useAppStore } from "hooks/utilsHooks/storeHooks";
import { createContext, type PropsWithChildren, useCallback, useContext, useEffect, useMemo, useRef } from "react";
import { useIdleTimerContext } from "react-idle-timer";
import { batch } from "react-redux";
import { Config } from "services/configuration/eatSmartConfig";
import { setQrCode } from "slices/accessSlice";
import { setAppState } from "slices/appStateSlice";
import { setDishTypes, setDishTypesLighting } from "slices/dishesSlice";
import { setDoorLockState, setDoorState, setHasDoorBeenOpenedDuringOrder, setLockState } from "slices/doorSlice";
import { setFridgeState } from "slices/fridgeInfoSlice";
import { setLightingMode } from "slices/fridgeLightingSlice";
import { isActiveOrderSlot, setTransaction } from "slices/transactionSlice";
import {
	DoorState,
	type FridgeSubscription,
	LightingSegmentModeType,
	type Subscription,
	TransactionMode,
	TransactionState,
} from "types";
import { AppState, type LocalFridgeState } from "types/mainTypes";
import { getFridgeId, getTabletTypeFromUrl, validateFridgeId } from "utils/browser/localStorage";
import { debugTrace, shouldTraceLightingDebug } from "utils/debug/debugTrace";
import { networkTrace, shouldTraceNetworkDebug } from "utils/debug/networkTrace";
import { FRIDGE_SUBSCRIPTION } from "utils/gqlOperations";
import { logToSentry } from "utils/logger";
import { getRefillTransitions } from "utils/refillTransitions";
import { deepOmit, isLightingEchoFrame } from "utils/utils";
import { handleSubscriptionTermination } from "./handleSubscriptionTermination";
import { resolveShouldSkipSubscription, shouldLogSkipFlip } from "./resolveShouldSkipSubscription";
import { useClearBrokenOnInbound } from "./useClearBrokenOnInbound";
import { useReconnectAfterBrokenSubscription } from "./useReconnectAfterBrokenSubscription";
import { useReloadOnUnrecoveredSubscription } from "./useReloadOnUnrecoveredSubscription";
import { useSubscriptionLivenessWatchdog } from "./useSubscriptionLivenessWatchdog";

export type DoorStateType = {
	state: DoorState | undefined;
	lock: {
		state: LockState | undefined;
		timeout: number | undefined | null;
	};
};

export type DeviceContextType = {
	id: string;
	loading: boolean;
	appConnected: boolean;
	handleRefetchDishTypes: () => Promise<void>;
	door: DoorStateType;
	initialQueryData: QueryResult | null;
};

type DeviceProviderProps = {
	client: ApolloClient<NormalizedCacheObject> | null;
	appConnected: boolean;
	setIsConnected: (newState: boolean) => void;
	isSubscriptionBroken: boolean;
	setIsSubscriptionBroken: (newState: boolean) => void;
};

const initialState: DeviceContextType = {
	id: "",
	loading: true,
	appConnected: true,
	handleRefetchDishTypes: () => Promise.resolve(),
	door: {
		state: DoorState.Closed,
		lock: {
			state: LockState.Locked,
			timeout: 0,
		},
	},
	initialQueryData: null,
};

export const DeviceContext = createContext<DeviceContextType>({
	...initialState,
	appConnected: true,
	loading: true,
});

export const DeviceContextProvider = ({
	children,
	appConnected,
	setIsConnected,
	isSubscriptionBroken,
	setIsSubscriptionBroken,
}: PropsWithChildren<DeviceProviderProps>) => {
	getTabletTypeFromUrl();
	const fridgeId = getFridgeId() ?? "";
	const appDispatch = useAppDispatch();
	// Used by onData for synchronous prev/next slot reads — dispatch is synchronous, so
	// store.getState() is never stale, unlike a closed-over selector value when rapid
	// subscription pushes arrive before React re-renders.
	const store = useAppStore();
	const idleTimer = useIdleTimerContext();
	const { doors, lock, lockTimeout } = useDoorAndLockState();
	const { trackDoorStateChange, trackRefillStarted, trackRefillFinished } = useMixpanelTrackers();
	const { getUnknownDishTypes } = useFetchUnknownDishTypes();
	const lastTrackedDoorStateRef = useRef<DoorState | null>(null);

	const {
		data,
		loading: initialQueryLoading,
		refetch: initialQueryRefetch,
	} = useInitialStateQuery({
		fridgeId: fridgeId,
	});
	const { loading: refetchDishTypesLoading, handleRefetchDishTypes } = useRefetchDishTypesQuery({
		fridgeId: fridgeId,
	});
	// Records the QR a transaction started with vs. the one shown once it ended, AND self-heals: at the
	// finished edge it refetches a fresh code when a (remote-refill) QR didn't rotate, so the next
	// customer never scans the previous one's code.
	useQrCodeLifecycleLogging({ fridgeId: fridgeId });

	// Skip is a latched startup gate (see resolveShouldSkipSubscription). hasStartedRef latches true
	// the first time the initial load yields a usable fridge, so a later transient refetch error can't
	// re-skip and strand a running subscription.
	const isFridgeIdValid = validateFridgeId(fridgeId);
	const hasValidFridge = Boolean(data?.fridge) && !data?.fridge?.error && isFridgeIdValid;
	const hasStartedRef = useRef(false);
	if (hasValidFridge) {
		hasStartedRef.current = true;
	}
	const isUnauthorized = data?.fridge?.error?.type === "UNAUTHORIZED";
	const shouldSkipSubscription = resolveShouldSkipSubscription({
		isFridgeIdValid: isFridgeIdValid,
		hasValidFridge: hasValidFridge,
		isUnauthorized: isUnauthorized,
		hasStarted: hasStartedRef.current,
	});

	// Read by the onComplete/onError callbacks below, which would otherwise close over a stale value.
	const shouldSkipRef = useRef(shouldSkipSubscription);
	shouldSkipRef.current = shouldSkipSubscription;

	// Instrumentation (root cause #1): a runtime skip-flip false→true after startup is the signature the
	// latch is meant to make impossible. Log it (with the causing fridge.error) so the field confirms the
	// fix held — and surfaces any new path that strands a running stream.
	const prevSkipRef = useRef(shouldSkipSubscription);
	useEffect(() => {
		if (shouldLogSkipFlip(hasStartedRef.current, prevSkipRef.current, shouldSkipSubscription)) {
			const fridgeError = data?.fridge?.error;
			logToSentry(
				`Subscription skip latched true after startup (unauthorized=${isUnauthorized}, validId=${isFridgeIdValid}), fridge: ${fridgeId}`,
				"warning",
				fridgeError ? [fridgeError] : [],
			);
		}
		prevSkipRef.current = shouldSkipSubscription;
	}, [shouldSkipSubscription, isUnauthorized, isFridgeIdValid, fridgeId, data?.fridge?.error]);

	const { restart } = useSubscription<Subscription>(FRIDGE_SUBSCRIPTION, {
		// If there is an error in the initial state query, we do not connect subscription
		skip: shouldSkipSubscription,
		variables: { id: fridgeId },
		// By using onData callback, we handle situation when messages from API
		// are delivered in the same time
		onData: ({ data }) => {
			if (data?.data?.fridge?.error) {
				logToSentry(`Error is subscription, fridge: ${fridgeId}`, "error", [data.data.fridge.error]);
			}
			// TEMP network diagnostic: a frame arriving is the "subscription healthy / reconnected"
			// signal. Paired with the [ws] BROKEN / reconnect lines it shows the broken→recovered story.
			if (shouldTraceNetworkDebug()) {
				networkTrace(data?.data?.fridge?.error ? "[ws] frame error" : "[ws] frame received");
			}
			setIsSubscriptionBroken(false);
			// A live subscription frame is the most authoritative "we're connected" signal. Now that a
			// subscription error flips us offline on the FIRST error (resolveRetryAttempt), recover the
			// instant data flows again instead of waiting on the Google-DNS ping — and clear any false
			// offline left by a non-subscription query failure while the stream was actually healthy.
			setIsConnected(true);
			const cleanedData = deepOmit<Maybe<FridgeSubscription> | undefined, LocalFridgeState>(data.data?.fridge, [
				"__typename",
			]);
			const transaction = cleanedData.transaction;
			const isLastOrderFinished = transaction?.orders?.last?.state === TransactionState.Finished;
			const hasDishTypes = cleanedData?.dishes?.types;
			// A spotlight TOGGLE echo is a SPARSE single-type frame (lighting.mode set, `dishes: null`). It
			// must NOT go through the snapshot reconcile, which would empty every still-stocked type the echo
			// omits — the master Meal List wipe on detail-open. Route it to a lighting-only update instead.
			const isLightingEcho = isLightingEchoFrame(hasDishTypes);

			// TEMP diagnostic: log what each FRIDGE_SUBSCRIPTION frame actually carries for lighting, so a
			// capture shows the BE-pushed state directly (not just the derived Redux writes). Pair with the
			// converger's FIRE-SWITCH / SWITCH-OK / FIRE-TOGGLE lines to see send vs ack vs subscription.
			if (shouldTraceLightingDebug()) {
				const dishTypes = cleanedData.dishes?.types ?? null;
				const spotlitIds = dishTypes
					? dishTypes
							.filter((dishType) => dishType?.lighting?.mode === LightingSegmentModeType.Spotlight)
							.map((dishType) => dishType?.id ?? "?")
					: null;
				// ids (not just count) so a BE-pushed spotlight set wider than the FE's FIRE-TOGGLE target names the stray segment.
				// orderPrefs = the customer allergen preference the BE put ON the order frame — empty means the BE
				// did not hand the FE the preference (so the FE cannot merge it; if dishes still light up, the BE applied it itself).
				const orderPrefs = transaction?.orders?.last?.user?.preferences?.allergens ?? null;
				debugTrace(
					`[state-debug] SUB lighting.mode=${cleanedData.lighting?.mode ?? "-"} dishTypes=${dishTypes?.length ?? "-"} spot=${spotlitIds?.length ?? "-"} spotIds=[${spotlitIds?.join(",") ?? ""}] order=${transaction?.orders?.last?.state ?? "-"} orderPrefs=[${orderPrefs?.join(",") ?? ""}] refill=${transaction?.refills?.last?.state ?? "-"} door=${cleanedData.door?.state ?? cleanedData.door?.lock?.state ?? "-"}`,
				);
			}

			batch(() => {
				if (cleanedData.state != null) {
					appDispatch(setFridgeState(cleanedData.state));
				}

				const hasDoorStateChange = cleanedData.door?.lock?.state;

				if (cleanedData.access?.qrCode?.code) {
					appDispatch(setQrCode(cleanedData.access.qrCode.code));
				}
				if (hasDoorStateChange) {
					if (cleanedData.door.state && cleanedData.door.state !== lastTrackedDoorStateRef.current) {
						trackDoorStateChange(cleanedData.door.state);
						lastTrackedDoorStateRef.current = cleanedData.door.state;
					}
					appDispatch(setDoorLockState(cleanedData.door));
				} else if (cleanedData?.door?.state) {
					if (cleanedData.door.state !== lastTrackedDoorStateRef.current) {
						trackDoorStateChange(cleanedData.door.state);
						lastTrackedDoorStateRef.current = cleanedData.door.state;
					}
					appDispatch(setDoorState(cleanedData.door.state));
				} else if (cleanedData?.door?.lock?.state) {
					appDispatch(setLockState(cleanedData.door.lock?.state));
				}

				if (cleanedData.lighting?.mode != null) {
					appDispatch(setLightingMode(cleanedData.lighting.mode));
				}

				if (!isLightingEcho && cleanedData.dishes?.types) {
					getUnknownDishTypes(cleanedData.dishes.types);
				}

				// Hoisted so the dish-type reconcile below can fire at refill finish too (the BE sends
				// `dishes.types: null` when the whole fridge is emptied, so we need this to clear the last type).
				let isRefillFinished = false;
				if (transaction?.orders?.last || transaction?.refills?.last) {
					const prevSlot = store.getState().transaction.slot;
					appDispatch(
						setTransaction({
							order: transaction.orders?.last ?? null,
							refill: transaction.refills?.last ?? null,
						}),
					);
					const nextSlot = store.getState().transaction.slot;
					// A different transaction id taking the slot means a new flow starts: reset the door flag
					// (covers cross-kind takeovers too). hasDoorBeenOpenedDuringOrder is shared between order
					// and refill flows because useOrderSteps drives both via selectActiveLocalTransaction.
					if (prevSlot && nextSlot && prevSlot.transaction.id !== nextSlot.transaction.id) {
						appDispatch(setHasDoorBeenOpenedDuringOrder(false));
					}
					if (nextSlot?.kind === "refill") {
						const prevRefill = prevSlot?.kind === "refill" ? prevSlot.transaction : null;
						const { isNewRefill, isRefillFinished: refillFinished } = getRefillTransitions(
							prevRefill,
							nextSlot.transaction,
						);
						isRefillFinished = refillFinished;
						// Finished guard: reconnect echoes of old finished refills must not emit a spurious "started".
						if (isNewRefill && nextSlot.transaction.state !== TransactionState.Finished) {
							logToSentry(`Refill started on fridge: ${fridgeId}, mode: ${nextSlot.transaction.mode}`, "info");
							trackRefillStarted(nextSlot.transaction);
						}
						if (refillFinished) {
							logToSentry(`Refill finished on fridge: ${fridgeId}, mode: ${nextSlot.transaction.mode}`, "info");
							trackRefillFinished(nextSlot.transaction);
							if (nextSlot.transaction.mode === TransactionMode.Remote) {
								// The end of a remote refill counts as fresh presence (mirrors finishOrder):
								// re-arm the inactive countdown and wake appState so the fridge and screen
								// stay on for a FULL inactive timeout before the normal idle edge dims them.
								// Remote refills have no OrderStep5/finishOrder, so nothing else provides
								// this wake — without it the BE-lit fridge stayed ACTIVE forever after a
								// remote refill (HW capture 11. 6. 2026).
								idleTimer.start();
								appDispatch(setAppState(AppState.ACTIVE));
							}
						}
					}
				}

				if (isLightingEcho) {
					// Lighting-only echo: update per-type lighting.mode without touching the type set or any
					// `dishes` array, so the dish types the sparse frame omits stay on the Meal List.
					appDispatch(setDishTypesLighting({ dishTypes: hasDishTypes ?? [] }));
				} else if (hasDishTypes || isLastOrderFinished || isRefillFinished) {
					// `dishes.types` carries TWO different frame semantics. During an ACTIVE order the BE streams
					// SPARSE single-type deltas (absence = unchanged), so the reducer must merge only — emptying
					// absent types collapsed availableDishTypeIds to the one delta'd type (Meal List + lighting
					// collapse; 1.14.x regression of 4af3a56). Refills, order-finish and browse send the FULL
					// authoritative snapshot (absence = the last dish was taken → drop it), so they keep the
					// destructive reconcile. When the whole fridge is emptied the BE sends `dishes.types: null`, so
					// on a finished order/refill we fall back to an empty array to clear the final dish type too.
					appDispatch(
						setDishTypes({
							dishTypes: hasDishTypes ?? [],
							reconcileRemovals: !isActiveOrderSlot(store.getState().transaction.slot),
						}),
					);
				}
			});
		},
		onComplete: () =>
			handleSubscriptionTermination({
				reason: "complete",
				shouldSkip: shouldSkipRef.current,
				fridgeId: fridgeId,
				setIsSubscriptionBroken: setIsSubscriptionBroken,
			}),
		onError: () =>
			handleSubscriptionTermination({
				reason: "error",
				shouldSkip: shouldSkipRef.current,
				fridgeId: fridgeId,
				setIsSubscriptionBroken: setIsSubscriptionBroken,
			}),
	});

	// restart() throws (Apollo invariant 57) if the subscription is currently skipped, so guard it.
	const restartSubscription = useCallback(() => {
		if (!shouldSkipSubscription) {
			restart();
		}
	}, [restart, shouldSkipSubscription]);

	useReconnectAfterBrokenSubscription({
		isSubscriptionBroken: isSubscriptionBroken,
		appConnected: appConnected,
		fridgeId: fridgeId,
		refetch: initialQueryRefetch,
		onData: (fridge) => handleSetInitialData(fridge, appDispatch),
		restartSubscription: restartSubscription,
	});

	useSubscriptionLivenessWatchdog({
		appConnected: appConnected,
		shouldSkip: shouldSkipSubscription,
		isSubscriptionBroken: isSubscriptionBroken,
		fridgeId: fridgeId,
		staleThresholdMs: Config.connectionSettings.connectionTimeout,
		setIsSubscriptionBroken: setIsSubscriptionBroken,
	});

	useClearBrokenOnInbound({
		isSubscriptionBroken: isSubscriptionBroken,
		appConnected: appConnected,
		shouldSkip: shouldSkipSubscription,
		setIsSubscriptionBroken: setIsSubscriptionBroken,
	});

	useReloadOnUnrecoveredSubscription({
		isSubscriptionBroken: isSubscriptionBroken,
		appConnected: appConnected,
		fridgeId: fridgeId,
	});

	const loading = initialQueryLoading || refetchDishTypesLoading;
	// Memoized so consumers of useDeviceContext() re-render only when one of these inputs
	// changes, not on every provider render.
	const contextValue = useMemo<DeviceContextType>(
		() => ({
			id: fridgeId,
			appConnected: appConnected,
			loading: loading,
			door: {
				state: doors,
				lock: {
					state: lock,
					timeout: lockTimeout,
				},
			},
			handleRefetchDishTypes: handleRefetchDishTypes,
			initialQueryData: data,
		}),
		[fridgeId, appConnected, loading, doors, lock, lockTimeout, handleRefetchDishTypes, data],
	);

	return <DeviceContext.Provider value={contextValue}>{children}</DeviceContext.Provider>;
};

export const useDeviceContext = () => useContext(DeviceContext);
